import { useState, useEffect, useCallback } from 'react';
import { getTokenOrThrow, authenticatedFetch } from '../services/api';
import type { UserTier } from '../types';

const API_URL = import.meta.env.VITE_API_URL;

interface UserData {
  tier: UserTier;
  usageRemaining: number | null;
  isLoading: boolean;
}

interface UserState extends UserData {
  setUsageRemaining: (value: number | null) => void;
}

/**
 * Fetches the authenticated user's tier and remaining daily usage from
 * GET /v1/users/me on mount. On any failure it falls back to the safe
 * default (free tier, unknown usage) so the UI still renders.
 *
 * setUsageRemaining lets a tool update the count locally from an action
 * response — this keeps useUserState the single source of truth for usage
 * without a second API call.
 */
export function useUserState(): UserState {
  const [data, setData] = useState<UserData>({
    tier: 'free',
    usageRemaining: null,
    isLoading: true,
  });

  useEffect(() => {
    async function fetchUserState() {
      try {
        const token = await getTokenOrThrow();
        const res = await authenticatedFetch(
          `${API_URL}/v1/users/me`,
          { headers: {} },
          token
        );
        if (!res.ok) {
          setData(s => ({ ...s, isLoading: false }));
          return;
        }
        const body = await res.json();
        setData({
          tier: body.tier,
          usageRemaining: body.usage_remaining,
          isLoading: false,
        });
      } catch {
        setData(s => ({ ...s, isLoading: false }));
      }
    }
    fetchUserState();
  }, []);

  const setUsageRemaining = useCallback((value: number | null) => {
    setData(s => ({ ...s, usageRemaining: value }));
  }, []);

  return { ...data, setUsageRemaining };
}
